import { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import portalService from '../../services/portal';
import type { ReplyContext, EmailSignature, AttachmentPayload } from '../../types/portal';
import RecipientInput, { recipientsToString, stringToRecipients } from './RecipientInput';
import styles from './EmailComposeModal.module.css';

/* eslint-disable @typescript-eslint/no-explicit-any */

export type ComposeAction = 'reply' | 'reply_all' | 'forward' | 'new';

/** What a successful send was: the pages use it to ask about an open Merlin card. */
export interface SentEmailSummary {
  action: ComposeAction;
  /** The CSR's own typed text (not the quoted original). */
  bodyText: string;
  /** Bare addresses the email went to (To + Cc), so the pages can tell a
   *  forward that went back to the sender from one that went elsewhere. */
  toAddresses: string[];
}

interface Recipient {
  email: string;
  name: string | null;
  company: string | null;
}

/** Local attachment state — extends the server payload with upload progress. */
interface AttachmentFile extends AttachmentPayload {
  uploading?: boolean;
  error?: string;
}

interface EmailComposeModalProps {
  /** SyncedEmail UUID to reply to (null for new compose) */
  emailId: string | null;
  /** Which action opened this modal */
  action: ComposeAction;
  /** Called when modal should close */
  onClose: () => void;
  /** Called after email is sent successfully (isSandbox=true if sandboxed) */
  onSent?: (isSandbox?: boolean, sent?: SentEmailSummary) => void;
}

/** Format bytes to human-readable string. */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Get a short label for a MIME type. */
function fileTypeLabel(contentType: string): string {
  const map: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/vnd.ms-powerpoint': 'PPT',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
    'text/plain': 'TXT',
    'text/csv': 'CSV',
    'application/zip': 'ZIP',
    'application/rtf': 'RTF',
  };
  return map[contentType] || contentType.split('/').pop()?.toUpperCase() || 'FILE';
}

/** Badge color for file type. */
function fileTypeBadgeColor(contentType: string): string {
  if (contentType === 'application/pdf') return '#e74c3c';
  if (contentType.includes('spreadsheet') || contentType.includes('excel')) return '#27ae60';
  if (contentType.includes('word') || contentType === 'application/msword') return '#2980b9';
  if (contentType.includes('presentation') || contentType.includes('powerpoint')) return '#e67e22';
  if (contentType.startsWith('image/')) return '#8e44ad';
  return '#7f8c8d';
}

const EmailComposeModal: React.FC<EmailComposeModalProps> = ({
  emailId,
  action,
  onClose,
  onSent,
}) => {
  // Form state - recipients as structured arrays
  const [toRecipients, setToRecipients] = useState<Recipient[]>([]);
  const [ccRecipients, setCcRecipients] = useState<Recipient[]>([]);
  const [bccRecipients, setBccRecipients] = useState<Recipient[]>([]);
  const [subject, setSubject] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  // Sending state
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sandboxNotice, setSandboxNotice] = useState<string | null>(null);

  // Context / signature
  const [replyContext, setReplyContext] = useState<ReplyContext | null>(null);
  // Quoted reply text and the signature preview are collapsed by default
  // (Gmail-style) so the compose body gets the vertical space; both are
  // still always sent with the email.
  const [showQuoted, setShowQuoted] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [signatures, setSignatures] = useState<EmailSignature[]>([]);
  const [selectedSignature, setSelectedSignature] = useState<EmailSignature | null>(null);
  const [sendingFrom, setSendingFrom] = useState<string | null>(null);
  const [sandboxRedirectEmail, setSandboxRedirectEmail] = useState<string>('');

  // Image upload state
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Ref to hold the upload function so editorProps can reference it
  // without a circular dependency on the editor variable.
  const handleImageUploadRef = useRef<(file: File) => void>(() => {});

  // Attachment state
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          style: 'max-width: 100%; height: auto;',
        },
        resize: {
          enabled: true,
          directions: ['top', 'bottom', 'left', 'right'],
          minWidth: 50,
          minHeight: 50,
          alwaysPreserveAspectRatio: true,
        },
      }),
      Placeholder.configure({
        placeholder: 'Write your message...',
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap',
      },
      handleDrop: (_view, event, _slice, moved) => {
        if (moved || !event.dataTransfer?.files.length) return false;
        const file = event.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
          event.preventDefault();
          handleImageUploadRef.current(file);
          return true;
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              handleImageUploadRef.current(file);
              return true;
            }
          }
        }
        return false;
      },
    },
  });

  // Upload an image file and insert into editor
  const handleImageUpload = useCallback(async (file: File) => {
    if (!editor) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Unsupported image type. Use PNG, JPEG, GIF, or WebP.');
      return;
    }
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (file.size > maxSize) {
      setError('Image too large. Maximum size is 5 MB.');
      return;
    }
    setImageUploading(true);
    setError(null);
    try {
      const publicUrl = await portalService.uploadComposeImage(file);
      editor.chain().focus().setImage({ src: publicUrl, alt: file.name }).run();
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  }, [editor]);

  // Keep the ref in sync with the latest handleImageUpload
  handleImageUploadRef.current = handleImageUpload;

  // Upload a file attachment
  const handleAttachmentUpload = useCallback(async (file: File) => {
    const maxSize = 25 * 1024 * 1024; // 25 MB
    if (file.size > maxSize) {
      setError(`File "${file.name}" is too large. Maximum size is 25 MB.`);
      return;
    }

    // Add placeholder entry while uploading
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const placeholder: AttachmentFile = {
      file_id: tempId,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      size_bytes: file.size,
      url: '',
      uploading: true,
    };
    setAttachments((prev) => [...prev, placeholder]);
    setError(null);

    try {
      const result = await portalService.uploadEmailAttachment(file);
      setAttachments((prev) =>
        prev.map((a) =>
          a.file_id === tempId ? { ...result, uploading: false } : a,
        ),
      );
    } catch (err: any) {
      setAttachments((prev) =>
        prev.map((a) =>
          a.file_id === tempId
            ? { ...a, uploading: false, error: err.message || 'Upload failed' }
            : a,
        ),
      );
      setError(err.message || `Failed to upload "${file.name}"`);
    }
  }, []);

  // Remove an attachment
  const removeAttachment = useCallback((fileId: string) => {
    setAttachments((prev) => prev.filter((a) => a.file_id !== fileId));
  }, []);

  // Handle files dropped on the modal (non-image files become attachments)
  const handleModalDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      for (const file of files) {
        if (file.type.startsWith('image/')) {
          // Images dropped outside the editor go as attachments too
          handleAttachmentUpload(file);
        } else {
          handleAttachmentUpload(file);
        }
      }
    },
    [handleAttachmentUpload],
  );

  const handleModalDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleModalDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only leave if we're actually leaving the modal (not entering a child)
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const { clientX, clientY } = e;
    if (
      clientX < rect.left ||
      clientX > rect.right ||
      clientY < rect.top ||
      clientY > rect.bottom
    ) {
      setIsDragOver(false);
    }
  }, []);

  // Prevent browser default file-navigation on drag/drop while modal is open.
  // Without this, dropping a file outside the modal div causes the browser to
  // navigate to the file URL (opening it in the current tab).
  useEffect(() => {
    const prevent = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', prevent);
    document.addEventListener('drop', prevent);
    return () => {
      document.removeEventListener('dragover', prevent);
      document.removeEventListener('drop', prevent);
    };
  }, []);

  // Load reply context
  useEffect(() => {
    if (emailId && action !== 'new') {
      portalService
        .getReplyContext(emailId, action)
        .then((ctx) => {
          setReplyContext(ctx);
          setToRecipients(stringToRecipients(ctx.to));
          if (ctx.cc) {
            setCcRecipients(stringToRecipients(ctx.cc));
            setShowCc(true);
          }
          if (ctx.sending_from) {
            // Replies go out from the mailbox that owns the thread, which
            // may differ from the default orders sending account.
            setSendingFrom(ctx.sending_from);
          }
          setSubject(ctx.subject);
          // Focus editor for typing — quoted content is displayed
          // separately below the editor (not inside TipTap).
          if (editor) {
            editor.commands.focus('start');
          }
        })
        .catch((err) => {
          setError(`Failed to load reply context: ${err.message || 'Unknown error'}`);
        });
    }
  }, [emailId, action]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus editor once it's ready (if context loaded first)
  useEffect(() => {
    if (editor && replyContext) {
      editor.commands.focus('start');
    }
  }, [editor, replyContext]);

  // Load signatures, sending account, and sandbox config
  useEffect(() => {
    portalService.listSignatures().then((sigs) => {
      setSignatures(sigs);
      const defaultSig = sigs.find((s) => s.is_default) || sigs[0] || null;
      setSelectedSignature(defaultSig);
    }).catch(() => { /* no signatures yet */ });

    portalService.getSendingAccount().then((resp) => {
      // Reply context may have already set the thread's own account —
      // don't overwrite it with the generic default.
      setSendingFrom((prev) => prev ?? resp.email);
    }).catch(() => { /* ignore */ });

    portalService.getPortalConfig().then((config) => {
      setSandboxRedirectEmail(config.sandbox_redirect_email || '');
    }).catch(() => { /* ignore */ });
  }, []);

  // Does the composer hold anything the CSR typed? The editor starts empty
  // (quoted text and the signature render outside it), so any text in it was
  // typed here. Recipients count for a new email or a forward (a reply has
  // them prefilled); the subject only for a new email (replies and forwards
  // prefill it). Attachments always count.
  const hasDraftContent = useCallback((): boolean => {
    if (editor && editor.getText().trim().length > 0) return true;
    if (attachments.length > 0) return true;
    if (action === 'new' || action === 'forward') {
      if (toRecipients.length > 0 || ccRecipients.length > 0 || bccRecipients.length > 0) return true;
    }
    if (action === 'new' && subject.trim().length > 0) return true;
    return false;
  }, [editor, attachments, action, toRecipients, ccRecipients, bccRecipients, subject]);

  // Close from the header button or Escape. A draft with content asks first
  // (kanban 2c097936): Escape is easy to hit while typing and the close
  // button sits next to the body, so neither may silently drop a reply. The
  // footer's explicit Discard button still closes at once.
  const requestClose = useCallback(() => {
    if (hasDraftContent() && !window.confirm('Discard this draft?')) return;
    onClose();
  }, [hasDraftContent, onClose]);

  // Close on Escape key (only when dropdown isn't open - RecipientInput handles its own Escape)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // The composer owns Escape while it is open: mark the key spent so the
      // email, order or split view underneath never closes on the same press
      // and takes the half-written reply with it.
      e.preventDefault();
      if (sending) return;
      // Only close if no autocomplete dropdown is open
      const dropdown = document.querySelector('[role="listbox"]');
      if (!dropdown) {
        requestClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [requestClose, sending]);

  // Send handler
  const handleSend = useCallback(async () => {
    if (toRecipients.length === 0) {
      setError('Recipient (To) is required');
      return;
    }
    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }

    // Check for still-uploading attachments
    const uploading = attachments.filter((a) => a.uploading);
    if (uploading.length > 0) {
      setError('Please wait for attachments to finish uploading.');
      return;
    }

    // Check for failed attachments
    const failed = attachments.filter((a) => a.error);
    if (failed.length > 0) {
      setError('Some attachments failed to upload. Remove them or try again.');
      return;
    }

    const toStr = recipientsToString(toRecipients);
    const ccStr = recipientsToString(ccRecipients) || null;
    const bccStr = recipientsToString(bccRecipients) || null;
    const toAddresses = [...toRecipients, ...ccRecipients].map((r) => r.email);

    setSending(true);
    setError(null);

    try {
      const bodyHtml = editor?.getHTML() || '';
      const bodyText = editor?.getText() || '';

      // Build attachment payloads (only successfully uploaded ones)
      const attachmentPayloads: AttachmentPayload[] | undefined =
        attachments.length > 0
          ? attachments
              .filter((a) => !a.uploading && !a.error)
              .map(({ file_id, filename, content_type, size_bytes, url }) => ({
                file_id,
                filename,
                content_type,
                size_bytes,
                url,
              }))
          : undefined;

      const result = await portalService.sendEmail({
        to: toStr,
        subject: subject.trim(),
        body_text: bodyText,
        body_html: bodyHtml,
        cc: ccStr,
        bcc: bccStr,
        signature_html: selectedSignature?.signature_html || null,
        quoted_html: replyContext?.quoted_html || null,
        in_reply_to_email_id: emailId,
        action: action,
        attachments: attachmentPayloads || null,
      });

      if (result.status === 'failed') {
        setError(result.error || 'Failed to send email');
        setSending(false);
        return;
      }

      if (result.status === 'sandbox') {
        // Show sandbox notice briefly, then close
        const notice = sandboxRedirectEmail
          ? `Sandbox: email redirected to ${sandboxRedirectEmail}`
          : `Sandbox: email to ${toStr} logged (not sent)`;
        setSandboxNotice(notice);
        setTimeout(() => {
          onSent?.(true, { action, bodyText, toAddresses });
          onClose();
        }, 1500);
        return;
      }

      onSent?.(false, { action, bodyText, toAddresses });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to send email');
      setSending(false);
    }
  }, [toRecipients, ccRecipients, bccRecipients, subject, editor, selectedSignature, replyContext, emailId, action, onSent, onClose, sandboxRedirectEmail, attachments]);

  const actionLabel = action === 'forward' ? 'Forward' : action === 'new' ? 'New Email' : action === 'reply_all' ? 'Reply All' : 'Reply';

  // No click-off close (kanban 2c097936): a click outside the composer must
  // never discard the reply being written.
  return (
    <div className={styles.overlay}>
      <div
        className={`${styles.modal} ${isDragOver ? styles.modalDragOver : ''}`}
        style={{ position: 'relative' }}
        onDrop={handleModalDrop}
        onDragOver={handleModalDragOver}
        onDragLeave={handleModalDragLeave}
      >
        {/* Drag overlay indicator */}
        {isDragOver && (
          <div className={styles.dragOverlay}>
            <div className={styles.dragOverlayContent}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
              </svg>
              <span>Drop files to attach</span>
            </div>
          </div>
        )}

        {/* Sending overlay */}
        {sending && (
          <div className={styles.sendingOverlay}>
            <span className={styles.sendingText}>Sending...</span>
          </div>
        )}

        {/* Header */}
        <div className={styles.header}>
          <div>
            <div className={styles.headerTitle}>{actionLabel}</div>
            {sendingFrom && (
              <div className={styles.headerFrom}>From: {sendingFrom}</div>
            )}
          </div>
          <button
            className={styles.closeBtn}
            onClick={requestClose}
            type="button"
            title="Close"
            aria-label="Close"
            data-action="email_compose_close"
            data-target-email-id={emailId ?? undefined}
            data-record-value="mode"
            value={action}
            data-outcome="close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Error */}
        {error && <div className={styles.error}>{error}</div>}

        {/* Sandbox notice */}
        {sandboxNotice && (
          <div className={styles.error} style={{ background: '#fff3e0', color: '#e65100', borderColor: '#ffb74d' }}>
            &#x1F9EA; {sandboxNotice}
          </div>
        )}

        {/* Recipient fields */}
        <div className={styles.fields}>
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>To</span>
            <RecipientInput
              recipients={toRecipients}
              onChange={setToRecipients}
              placeholder="Search contacts or type email..."
            />
            {!showCc && (
              <button className={styles.ccToggle} onClick={() => setShowCc(true)} type="button">
                Cc
              </button>
            )}
            {!showBcc && (
              <button className={styles.ccToggle} onClick={() => setShowBcc(true)} type="button">
                Bcc
              </button>
            )}
          </div>

          {showCc && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>Cc</span>
              <RecipientInput
                recipients={ccRecipients}
                onChange={setCcRecipients}
                placeholder="Search contacts or type email..."
              />
            </div>
          )}

          {showBcc && (
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>Bcc</span>
              <RecipientInput
                recipients={bccRecipients}
                onChange={setBccRecipients}
                placeholder="Search contacts or type email..."
              />
            </div>
          )}

          <input
            className={styles.subjectInput}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
          />
        </div>

        {/* Toolbar */}
        <div className={styles.toolbar}>
          <button
            className={`${styles.toolbarBtn} ${editor?.isActive('bold') ? styles.toolbarBtnActive : ''}`}
            onClick={() => editor?.chain().focus().toggleBold().run()}
            type="button"
            title="Bold"
          >
            <strong>B</strong>
          </button>
          <button
            className={`${styles.toolbarBtn} ${editor?.isActive('italic') ? styles.toolbarBtnActive : ''}`}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            type="button"
            title="Italic"
          >
            <em>I</em>
          </button>
          <button
            className={`${styles.toolbarBtn} ${editor?.isActive('underline') ? styles.toolbarBtnActive : ''}`}
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            type="button"
            title="Underline"
          >
            <u>U</u>
          </button>
          <div className={styles.toolbarDivider} />
          <button
            className={`${styles.toolbarBtn} ${editor?.isActive('bulletList') ? styles.toolbarBtnActive : ''}`}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            type="button"
            title="Bullet List"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
              <circle cx="4" cy="6" r="1" fill="currentColor" /><circle cx="4" cy="12" r="1" fill="currentColor" /><circle cx="4" cy="18" r="1" fill="currentColor" />
            </svg>
          </button>
          <button
            className={`${styles.toolbarBtn} ${editor?.isActive('orderedList') ? styles.toolbarBtnActive : ''}`}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            type="button"
            title="Numbered List"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" />
              <text x="2" y="8" fontSize="8" fill="currentColor" stroke="none">1</text>
              <text x="2" y="14" fontSize="8" fill="currentColor" stroke="none">2</text>
              <text x="2" y="20" fontSize="8" fill="currentColor" stroke="none">3</text>
            </svg>
          </button>
          <div className={styles.toolbarDivider} />
          <button
            className={`${styles.toolbarBtn} ${editor?.isActive('blockquote') ? styles.toolbarBtnActive : ''}`}
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
            type="button"
            title="Quote"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="3" y2="18" /><line x1="8" y1="8" x2="20" y2="8" /><line x1="8" y1="12" x2="18" y2="12" /><line x1="8" y1="16" x2="14" y2="16" />
            </svg>
          </button>
          <button
            className={styles.toolbarBtn}
            onClick={() => {
              const url = window.prompt('URL:');
              if (url) editor?.chain().focus().setLink({ href: url }).run();
            }}
            type="button"
            title="Insert Link"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </button>
          <div className={styles.toolbarDivider} />
          <button
            className={styles.toolbarBtn}
            onClick={() => fileInputRef.current?.click()}
            type="button"
            title="Insert Image"
            disabled={imageUploading}
          >
            {imageUploading ? (
              <span style={{ fontSize: 11 }}>...</span>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImageUpload(file);
              // Reset input so the same file can be re-selected
              e.target.value = '';
            }}
          />

          {/* Attach file button */}
          <button
            className={styles.toolbarBtn}
            onClick={() => attachmentInputRef.current?.click()}
            type="button"
            title="Attach File"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          <input
            ref={attachmentInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rtf,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.tiff"
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              for (const file of files) {
                handleAttachmentUpload(file);
              }
              e.target.value = '';
            }}
          />

          {/* Signature selector (if multiple) */}
          {signatures.length > 1 && (
            <>
              <div className={styles.toolbarDivider} />
              <select
                value={selectedSignature?.id || ''}
                onChange={(e) => {
                  const sig = signatures.find((s) => s.id === e.target.value) || null;
                  setSelectedSignature(sig);
                }}
                style={{ fontSize: 12, border: '1px solid #ddd', borderRadius: 4, padding: '2px 6px', color: 'var(--gray)' }}
              >
                <option value="">No signature</option>
                {signatures.map((sig) => (
                  <option key={sig.id} value={sig.id}>
                    {sig.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>

        {/* Editor */}
        <div className={styles.editorWrapper}>
          <div className={styles.editor}>
            <EditorContent editor={editor} />
          </div>
        </div>

        {/* Attachments list */}
        {attachments.length > 0 && (
          <div className={styles.attachmentSection}>
            <div className={styles.attachmentLabel}>
              Attachments ({attachments.length})
            </div>
            <div className={styles.attachmentList}>
              {attachments.map((att) => (
                <div
                  key={att.file_id}
                  className={`${styles.attachmentItem} ${att.error ? styles.attachmentItemError : ''}`}
                >
                  <span
                    className={styles.attachmentBadge}
                    style={{ background: fileTypeBadgeColor(att.content_type) }}
                  >
                    {fileTypeLabel(att.content_type)}
                  </span>
                  <span className={styles.attachmentName} title={att.filename}>
                    {att.filename}
                  </span>
                  <span className={styles.attachmentSize}>
                    {att.uploading ? 'Uploading...' : att.error ? 'Failed' : formatFileSize(att.size_bytes)}
                  </span>
                  <button
                    className={styles.attachmentRemove}
                    onClick={() => removeAttachment(att.file_id)}
                    type="button"
                    title="Remove"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signature preview (collapsed by default; always appended on send) */}
        {selectedSignature && (
          <div className={styles.signatureSection}>
            <button
              type="button"
              className={styles.quotedToggle}
              onClick={() => setShowSignature((v) => !v)}
              aria-expanded={showSignature}
              title={showSignature ? 'Hide signature' : 'Show signature'}
            >
              <span className={styles.quotedToggleDots}>✎</span>
              {showSignature ? 'Hide signature' : 'Show signature'}
            </button>
            {showSignature && (
              <div
                className={styles.signaturePreview}
                dangerouslySetInnerHTML={{ __html: selectedSignature.signature_html }}
              />
            )}
          </div>
        )}

        {/* Quoted content (displayed separately from editor; always sent) */}
        {replyContext?.quoted_html && (
          <div className={styles.quotedSection}>
            <button
              type="button"
              className={styles.quotedToggle}
              onClick={() => setShowQuoted((v) => !v)}
              aria-expanded={showQuoted}
              title={showQuoted ? 'Hide quoted text' : 'Show quoted text'}
            >
              <span className={styles.quotedToggleDots}>⋯</span>
              {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
            </button>
            {showQuoted && (
              <div
                className={styles.quotedContent}
                dangerouslySetInnerHTML={{ __html: replyContext.quoted_html }}
              />
            )}
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <button
            className={styles.sendBtn}
            onClick={handleSend}
            disabled={sending || toRecipients.length === 0}
            type="button"
            data-action="email_send"
            data-record-value="mode"
            value={action}
            data-target-email-id={emailId ?? undefined}
            data-outcome="request"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" />
            </svg>
            {sending ? 'Sending...' : 'Send'}
          </button>
          <div className={styles.footerActions}>
            {attachments.length > 0 && (
              <span className={styles.attachmentCount}>
                {attachments.length} file{attachments.length !== 1 ? 's' : ''} attached
              </span>
            )}
            <button className={styles.discardBtn} onClick={onClose} disabled={sending} type="button">
              Discard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailComposeModal;
